// components/common/Modal/Modal.tsx
import { ReactNode, ReactElement } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  DialogProps,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SxProps, Theme } from '@mui/material/styles';

type MaxWidth = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;

export interface ModalProps extends Omit<DialogProps, 'title'> {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  titleColor?: string;
  children: ReactNode;
  actions?: ReactNode;
  maxWidth?: MaxWidth;
  fullWidth?: boolean;
  showCloseButton?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Modal - Styled dialog component
 */
export function Modal({
  open,
  onClose,
  title,
  titleColor = '#00FFD4',
  children,
  actions,
  maxWidth = 'sm',
  fullWidth = true,
  showCloseButton = true,
  sx = {},
  ...props
}: ModalProps): ReactElement {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          backgroundColor: '#1E2530',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          backgroundImage: 'none',
          ...sx,
        },
      }}
      {...props}
    >
      {title && (
        <DialogTitle
          sx={{
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 600,
            color: titleColor,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: showCloseButton ? 1 : 3,
          }}
        >
          {title}
          {showCloseButton && (
            <IconButton
              onClick={onClose}
              aria-label="Close dialog"
              sx={{
                color: '#8892A0',
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: '#FF3366',
                  transform: 'rotate(90deg)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
      )}

      <DialogContent sx={{ pt: 5, color: '#FFFFFF', overflow: 'visible' }}>
        {children}
      </DialogContent>

      {actions && (
        <DialogActions sx={{ p: 2, gap: 1, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
}

export default Modal;
